/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { isEmpty, isNumber } from 'lodash';
import { makeStyles } from '@mui/styles';
import {
  Grid2,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Table,
  Divider,
  Button,
} from '@mui/material';
import { ContentQueryParams, ContentResponse, Folder, SubCategoryType } from '../../../../data/types/content.interface';
import Chips from '../../../shared/components/Chips';
import FilterModalForContent from '../../../shared/components/filterModalForContent';
import SearchBox from '../../../shared/components/SearchBox';
import SortableTableHead, { SortableTableHeadCell } from '../../../shared/components/SortableTableHead';
import usePaginatedContent from '../../../shared/components/usePaginatedContent';
import MarketingPlatformContentDetails from '../components/marketingPlatformContentDetails';
import { MarketingPlatforms } from '../../../shared/constants';
import ThemeService from '../../../tags/theme/services/theme.services';
import { TagType } from '../../../../data/types/tags.interface';
import MarketingContentRow from '../components/marketingContentRow';
import ContentsService from '../../../content-creation/services/contents.services';

const defaultSortDirection = 'asc';
const defaultSortColumn = 'filename';

const useStyles = makeStyles({
  header: {
    fontSize: '28px',
    color: '#303340',
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#30343F',
      color: '#fff !important',
      fontSize: '17px',
      '& .MuiTableSortLabel-root.Mui-active': {
        color: '#fff !important',
        '&:hover': {
          color: '#fff !important',
        },
      },
      '& .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
    },
  },
  clearAll: {
    color: '#30343F !important',
    fontFamily: 'Lato !important',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    lineHeight: 'normal !important',
  },
});

const YouTubeMarketingFiles: React.FC = () => {
  const classes = useStyles();
  const [columnHeader, setColumnHeader] = useState<SortableTableHeadCell<any>[]>([]);
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
  const [assetId, setAssetId] = useState<number | null>(null);
  const [assetType, setAssetType] = useState<string>('video');
  const [openFilters, setOpenFilters] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [refresh, setRefresh] = useState(false);
  const [themeList, setThemeList] = useState<TagType[]>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<Folder[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<SubCategoryType[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedThemes, setSelectedThemes] = React.useState<TagType[]>([]);
  const [selectedPublishStatus, setSelectedPublishStatus] = React.useState<string>();
  const {
    contentCollections,
    listFilterParams,
    setListFilterParams,
    isLoading,
    infiniteScrollRef,
    noMoreRecords,
    setNoMoreRecords,
    setIsReLoaded,
    folderNames,
    contentTypes,
    categories,
    subCategories,
  } = usePaginatedContent({ type: 'published', contentType: 'all', platform: [MarketingPlatforms[0]] });

  const hasMorePages = () => !noMoreRecords;

  const getAllThemes = async (type: string) => {
    const result = await ThemeService.get(type);
    if (type === 'theme') {
      setThemeList(result);
    }
  };

  useEffect(() => {
    setColumnHeader([
      { id: 'filename', label: 'Final File', numeric: false, sortable: true },
      { id: 'platform', label: 'Marketing Platform', numeric: false, sortable: true },
      { id: 'title', label: 'Title', numeric: false, sortable: true },
      { id: 'theme', label: 'Theme', numeric: false, sortable: true },
      { id: 'category', label: 'Category', numeric: false, sortable: true },
      { id: 'subCategory', label: 'Sub Category', numeric: false, sortable: true },
      { id: 'contentFolder', label: 'Content Folder', numeric: false, sortable: true },
      { id: 'publishedAt', label: 'Published Date', numeric: false, sortable: false },
      { id: 'publishStatus', label: 'Publish Status', numeric: false, sortable: true },
    ]);
    getAllThemes('theme');
  }, []);

  useEffect(() => {
    setSelectedFolder(listFilterParams.folder || []);
    setSelectedCategory(listFilterParams.category || []);
    setSelectedSubCategory(listFilterParams.subCategory || []);
    setStartDate(listFilterParams.startDate || null);
    setEndDate(listFilterParams.endDate || null);
    setSelectedThemes(listFilterParams.themes || []);
    setSelectedPublishStatus(listFilterParams.publishStatus || '');
  }, [listFilterParams]);

  useEffect(() => {
    if (!openDetailsModal) {
      setListFilterParams((prev) => ({ ...prev, assetType: contentTypes }));
    }
  }, [openDetailsModal]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      setNoMoreRecords(false);
      setIsReLoaded(false);
      setListFilterParams((prev) => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, sortField: any) => {
    setNoMoreRecords(false);
    setIsReLoaded(false);
    if (listFilterParams.sort === sortField) {
      const newDirection = listFilterParams.direction === 'asc' ? 'desc' : 'asc';
      setListFilterParams({
        ...listFilterParams,
        direction: newDirection,
        page: 1,
      });
    } else {
      setListFilterParams({
        ...listFilterParams,
        sort: sortField,
        direction: 'asc',
        page: 1,
      });
    }
  };

  const onSearchBoxSubmit = (q: string) => {
    setNoMoreRecords(false);
    setListFilterParams({
      ...listFilterParams,
      q,
      page: 1,
    });
    if (isEmpty(q)) {
      delete listFilterParams.q;
    }
  };

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const rowClickHandler = (params: any) => {
    setOpenDetailsModal(true);
    setAssetId(params.id);
    setAssetType(params?.assetContentType?.name);
  };

  const handleAppliedFilters = (params: ContentQueryParams) => {
    const props = { ...listFilterParams, ...params };
    listFilterParams.folder = params.folder;
    listFilterParams.platform = params.platform;
    listFilterParams.publishStatus = params.publishStatus;
    setListFilterParams(props);
    setOpenFilters(false);
  };

  const handleCloseFilters = () => {
    setOpenFilters(false);
    setListFilterParams({
      page: 1,
      type: '',
      sort: defaultSortColumn,
      direction: defaultSortDirection,
      perPage: 50,
    });
  };

  const fetchFilterCount = () => {
    return ContentsService.getAppliedFiltersCount(listFilterParams);
  };

  const updateAll = (newParams: any) => {
    if (newParams.hasOwnProperty('themes')) {
      listFilterParams.themes = newParams?.themes;
    }
    if (newParams.hasOwnProperty('subCategory')) {
      listFilterParams.subCategory = newParams?.subCategory;
    }
    if (newParams.hasOwnProperty('category')) {
      listFilterParams.category = newParams?.category;
    }
    if (newParams.hasOwnProperty('folder')) {
      listFilterParams.folder = newParams?.folder;
    }
    if (newParams.hasOwnProperty('endDate')) {
      listFilterParams.endDate = newParams?.endDate;
    }
    if (newParams.hasOwnProperty('startDate')) {
      listFilterParams.startDate = newParams?.startDate;
    }
    if (newParams.hasOwnProperty('publishStatus')) {
      listFilterParams.publishStatus = newParams?.publishStatus;
    }

    setListFilterParams({
      ...listFilterParams,
      page: 1,
    });
    setNoMoreRecords(false);
    setIsReLoaded(false);
  };

  const handleCancel = () => {
    setSelectedFolder([]);
    setSelectedCategory([]);
    setSelectedSubCategory([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedThemes([]);
    setSelectedPublishStatus('');
    handleCloseFilters();
  };

  return (
    <Grid2 container direction="column">
      <Grid2 container justifyContent="space-between" alignItems="center" height="84px">
        <Grid2 container pl="20px">
          <span className={classes.header}>YouTube</span>
        </Grid2>
        <Grid2 container justifyContent="flex-end" mr="10px">
          <Grid2 container direction="column">
            <FilterModalForContent
              handleFilters={handleAppliedFilters}
              open={openFilters}
              anchorEl={anchorEl}
              list={folderNames || []}
              contentTypes={[]}
              categories={categories || []}
              subCategories={subCategories || []}
              handleClose={handleCloseFilters}
              fileType="marketing"
              themeList={themeList}
              topicList={[]}
            />
          </Grid2>
          <Grid2 container>
            <SearchBox
              query={listFilterParams.q}
              onSubmit={onSearchBoxSubmit}
              isFiltersPresent
              filtersCount={fetchFilterCount()}
              width={180}
              openFilters={openFilters}
              setOpenFilters={setOpenFilters}
              setFiltersAnchorEl={handleFilterOpen}
              placeholder="Search files"
            />
          </Grid2>
        </Grid2>
      </Grid2>

      {fetchFilterCount() > 0 && listFilterParams && (
        <>
          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>
          <Grid2 container mb={1} height="45px" display="flex" alignItems="center" padding="10px 10px 10px 20px">
            <Chips
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedSubCategory={selectedSubCategory}
              setSelectedSubCategory={setSelectedSubCategory}
              selectedThemes={selectedThemes}
              setSelectedThemes={setSelectedThemes}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              selectedPublishStatus={selectedPublishStatus}
              setSelectedPublishStatus={setSelectedPublishStatus}
              updateAll={updateAll}
            />
            {fetchFilterCount() > 0 && (
              <Grid2 container>
                <Button size="small" onClick={handleCancel} className={classes.clearAll}>
                  <span style={{ fontSize: '12px', textTransform: 'none' }}>CLEAR ALL</span>
                </Button>
              </Grid2>
            )}
          </Grid2>
        </>
      )}

      <TableContainer className={classes.table} sx={{ overflow: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <SortableTableHead
            onRequestSort={handleRequestSort}
            order={listFilterParams.direction || defaultSortDirection}
            orderBy={listFilterParams.sort || defaultSortColumn}
            headCells={columnHeader}
            headerVariant="subtitle2"
            tableAssetType="marketing"
          />

          {contentCollections.length > 0 ? (
            <TableBody sx={{ color: '#303340' }}>
              {contentCollections.map((content: ContentResponse, index: number) => (
                <MarketingContentRow
                  key={`content-${content.id}`}
                  content={content}
                  index={index}
                  onRowClicked={() => rowClickHandler(content)}
                />
              ))}
              {(isLoading || hasMorePages()) && (
                <TableRow>
                  <TableCell colSpan={4} ref={infiniteScrollRef}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          ) : (
            <TableBody sx={{ color: '#303340' }}>
              <TableRow>
                <TableCell colSpan={8} align="center">
                  You have not yet created any collection. To begin, click the ‘Add File To Marketing’ button.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>

        {openDetailsModal && isNumber(assetId) && (
          <MarketingPlatformContentDetails
            setOpenDetailsModal={setOpenDetailsModal}
            id={assetId}
            assetType={assetType}
            setRefresh={setRefresh}
            target="youtube"
          />
        )}
      </TableContainer>
    </Grid2>
  );
};

export default YouTubeMarketingFiles;
